/**
 * The client portal's CRM-wide defaults, as the Settings panel reads them.
 *
 * Mirrors `apps/api/src/modules/settings/crm-portal.settings.ts` (the shape) and
 * `crm-portal.routes.ts` (the round trip). It carries the TOP level of the three
 * and nothing else.
 *
 * Note what is deliberately NOT here: the precedence rule. Which pages a
 * particular person at a particular company ends up with is resolved once, on
 * the server (`resolveModuleGrant`), and travels on the row that needs it. A
 * second implementation of that rule in the browser would be a second chance to
 * disagree with the gate, and the disagreement would be invisible - a screen
 * that overstates access answers 200 and looks calm.
 *
 * What the values MEAN lives with the vocabularies in `@wa/core`: `null` is "no
 * rule at this level", `[]` is "decided on nothing". They are one keystroke
 * apart in a JSON column and a world apart in effect, which is why every
 * function below keeps them distinct rather than normalising one into the other.
 */
import type { PortalModuleGrant } from "@wa/core/portal-modules";
import type { PortalProjectTabGrant } from "@wa/core/portal-project-tabs";
import { api } from "./api";

export interface CrmPortalSettings {
  /** Portal pages, unless the client row overrides it. `null` = no rule. */
  modules: PortalModuleGrant;
  /** Project-page tabs, unless the client row overrides it. `null` = no rule. */
  projectTabs: PortalProjectTabGrant;
}

/** What an installation that has never configured the portal answers with. */
export const EMPTY_PORTAL_DEFAULTS: CrmPortalSettings = { modules: null, projectTabs: null };

/** `view:crm` on the server - wider than the write, because the client detail
 *  screen echoes these defaults beside every per-client and per-contact grant. */
export const getPortalDefaults = () => api.get<CrmPortalSettings>("/admin/crm/portal-defaults");

/**
 * A partial write. `manage:crm.clients` on the server.
 *
 * The route MERGES, and the two absent-ish values do not mean the same thing:
 * an omitted field is left alone, an explicit `null` clears that grant to "no
 * rule". `JSON.stringify` drops keys whose value is `undefined`, so a caller
 * that spreads a half-built object gets the "leave it alone" behaviour for free
 * and only a deliberate `null` ever clears anything.
 */
export const putPortalDefaults = (patch: Partial<CrmPortalSettings>) =>
  api.put<CrmPortalSettings>("/admin/crm/portal-defaults", patch);

/**
 * The two states a level's grant can be edited in.
 *
 * Not three: `[]` is an ordinary custom grant that happens to be empty, and
 * giving it a mode of its own would let an operator leave "empty" by pressing a
 * button rather than by ticking something back on - which is how a level ends up
 * saying `["invoices"]` when the operator believed they had reset it.
 */
export type GrantMode = "inherit" | "custom";

/** `null` IS inherit. The only place the panel is allowed to say so. */
export function grantMode(grant: readonly unknown[] | null): GrantMode {
  return grant === null ? "inherit" : "custom";
}

/**
 * What "switch this level to custom" writes before the operator touches a pill.
 *
 * The WHOLE vocabulary, so the act of choosing to decide here does not itself
 * take anything away. Seeding from `[]` would mean one click on a mode button
 * closed the portal for every client that has no override of their own, and the
 * screen would look like it had merely opened an editor. At this level - the
 * top one - the whole vocabulary is also exactly what "no rule" was already
 * granting, so the seed changes the stored value without changing a single
 * client's access, which is the only honest way to enter an editor.
 */
export function seedCustomGrant<T extends string>(items: readonly T[]): T[] {
  return [...items];
}

/**
 * Are these two grants the same decision? Used to tell a dirty form from a clean
 * one, since this panel saves on a button rather than on every tick.
 *
 * `null` equals only `null`: an unset level and a level holding every name are
 * the same access today and different decisions tomorrow, and a Save button that
 * went grey between them would hide the difference at the moment it was made.
 *
 * The array compare is positional rather than set-based, which is safe because
 * both producers emit vocabulary order and nothing else ever builds one of these
 * by hand: `GrantPills` returns `items.filter(...)` and the server's parsers
 * return `PORTAL_MODULES.filter(...)` / `PORTAL_PROJECT_TABS.filter(...)`.
 */
export function grantsEqual<T extends string>(
  a: readonly T[] | null,
  b: readonly T[] | null,
): boolean {
  if (a === null || b === null) return a === b;
  return a.length === b.length && a.every((item, i) => item === b[i]);
}
